/*
 * ROREN - Sticky add-to-cart bar.
 *
 * The bar mirrors the product state but never posts to the cart itself.
 * It submits the real product form, which keeps quantity, selling plans,
 * line item properties and theme/app integrations intact.
 *
 * Variant changes are synced through the theme's pub/sub event
 * (PUB_SUB_EVENTS.variantChange), whose payload contains a freshly rendered
 * copy of this section. We copy the fresh inner markup so price, image and
 * availability always match the selected variant.
 */
(function () {
  if (customElements.get('sticky-atc')) return;

  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      // The theme clones this section into its quick-view dialog; the fixed
      // bar belongs to the product page only.
      if (this.closest('quick-add-modal, modal-dialog')) {
        this.remove();
        return;
      }

      this.sectionId = this.dataset.sectionId;
      this.visible = false;
      this.form = document.getElementById(`product-form-${this.sectionId}`);
      this.submitButton =
        document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
        this.closest('product-info')?.querySelector('form[action*="/cart/add"] [type="submit"]');

      this.onClick = this.onClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      this.addEventListener('click', this.onClick);
      this.subscribeToVariantChanges();
      this.observeOriginalButton();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.unsubscribe?.();
      this.observer?.disconnect();
      this.unsubscribe = null;
      this.observer = null;
      document.body.classList.remove('sticky-atc-visible');
    }

    subscribeToVariantChanges() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    }

    onVariantChange(event) {
      const payload = event?.detail?.data;
      if (!payload || payload.sectionId !== this.sectionId || !payload.html) return;

      const source = payload.html.querySelector(`#StickyATC-${this.sectionId}`);
      if (!source) return;

      // Keep the root element (and its visibility state), refresh its contents.
      this.innerHTML = source.innerHTML;
    }

    observeOriginalButton() {
      if (!this.submitButton || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          // Only show after the real button has been scrolled past, never
          // while it is still waiting below the fold.
          const shouldShow = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.setVisible(shouldShow);
        },
        { threshold: 0 }
      );

      this.observer.observe(this.submitButton);
    }

    setVisible(visible) {
      if (visible === this.visible) return;
      this.visible = visible;
      this.hidden = !visible;
      // Lets the stylesheet lift the third-party chat button clear of the bar.
      document.body.classList.toggle('sticky-atc-visible', visible);
    }

    onClick(event) {
      const button = event.target.closest('.sticky-atc__button');
      if (!button || button.disabled) return;

      const form =
        this.form && document.contains(this.form)
          ? this.form
          : document.getElementById(`product-form-${this.sectionId}`);
      const submitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

      if (!form || !submitButton) return;
      if (
        submitButton.disabled ||
        submitButton.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      // Use the theme's own submit handler so cart drawer/notification,
      // loading states, errors and analytics all behave normally.
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitButton);
      } else {
        submitButton.click();
      }
    }
  }

  customElements.define('sticky-atc', StickyAddToCart);
})();
